import { API } from "../../../services";
import { handleResponse } from "../../../helpers";
import { API_URL } from "../../../config/appConfig";
import { normalizeUserList } from "../utils/normalizeUser";

const basePath = `${API_URL.idp}/system/user`;

const getAllSystemUsers = async () => {
  return API.get(basePath, "system_users_list").then(handleResponse);
};

const getSystemUserById = async (userId) => {
  return API.get(`${basePath}/${userId}`, "system_user_by_id").then(handleResponse);
};

const getSystemUserByUsername = async (username) => {
  return API.get(
    `${basePath}/username/${encodeURIComponent(username)}`,
    "system_user_by_username"
  ).then(handleResponse);
};

const changeSystemUserPassword = async (userId, newPassword) => {
  // IAM: PUT …/system/user/changepassword/{id} with { password } (not newPassword).
  return API.put(
    `${basePath}/changepassword/${userId}`,
    "system_user_change_password",
    { password: newPassword }
  ).then(handleResponse);
};

const updateSystemUserRole = async (userId, roles) => {
  return API.put(`${basePath}/role/${userId}`, "system_user_update_role", {
    roles,
  }).then(handleResponse);
};

const createSystemUser = async (payload) => {
  return API.post(basePath, "system_user_create", {
    firstname: payload.firstname,
    lastname: payload.lastname || "",
    username: payload.username,
    email: payload.email,
    password: payload.password,
    roles: payload.roles,
    ...(payload.mobile ? { mobile: payload.mobile } : {}),
  }).then(handleResponse);
};

const updateSystemUser = async (userId, payload) => {
  // IAM rejects empty strings (min length 1). Omit optional lastname when blank.
  const lastname = String(payload.lastname || "").trim();
  const body = {
    firstname: payload.firstname,
    username: payload.username,
    email: payload.email,
    mobile: payload.mobile || "",
    enabled: Boolean(payload.enabled),
  };
  if (lastname) {
    body.lastname = lastname;
  }
  return API.put(`${basePath}/${userId}`, "system_user_update", body).then(
    handleResponse
  );
};

const deleteSystemUser = async (userId) => {
  if (!userId) {
    throw new Error("User id is missing.");
  }
  return API.delete(`${basePath}/${userId}`, "system_user_delete").then(
    handleResponse
  );
};

const roleAssignmentKeys = (role) => {
  if (role == null) return [];
  if (typeof role === "string") {
    const value = role.trim().toLowerCase();
    return value ? [value] : [];
  }
  return [role.name, role.roleId, role.id, role._id]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter((value, index, list) => value && list.indexOf(value) === index);
};

/**
 * Users store roles by name. After a role is deleted, that name can remain
 * on the user and re-bind if a new role reuses it. Strip it by User Id.
 */
const detachRoleFromSystemUsers = async (role) => {
  const keys = new Set(roleAssignmentKeys(role));
  if (!keys.size) return;

  const users = normalizeUserList(await getAllSystemUsers());
  const updates = users
    .map((user) => {
      const roles = Array.isArray(user.roles)
        ? user.roles.map(String).filter(Boolean)
        : [];
      const next = roles.filter(
        (item) => !keys.has(String(item).trim().toLowerCase())
      );
      if (next.length === roles.length) return null;
      const userId = user.userId || user.id;
      if (!userId) return null;
      return updateSystemUserRole(userId, next);
    })
    .filter(Boolean);

  if (updates.length) {
    await Promise.all(updates);
  }
};

export const systemUserService = {
  getAllSystemUsers,
  getSystemUserById,
  getSystemUserByUsername,
  changeSystemUserPassword,
  updateSystemUserRole,
  createSystemUser,
  updateSystemUser,
  deleteSystemUser,
  detachRoleFromSystemUsers,
};
